<script setup>
import axios from 'axios';
import { computed, onBeforeUnmount, onMounted, reactive, ref, toRef, watch } from 'vue';
import { isValidHttpUrl } from '../../../helpers/bannerHelper';
import XlButton from '../../xl/XlButton.vue';
import XlField from '../../xl/XlField.vue';
import ContentPageShell from '../../content/ContentPageShell.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import useHubBrokers from '../../../composables/useHubBrokers';
import useHubSegments from '../../../composables/useHubSegments';
import useStagedImage from '../../../composables/useStagedImage';
import { accessSummaryLine, bookFor, ownBookOf } from '../../../composables/useResourceAccess';
import { useUnsavedChanges } from '../../../composables/useUnsavedChanges';
import { useAuthStore } from '../../../stores/auth';

/**
 * The link editor: an optional image (XN-4775), a title, a description, an
 * address, and who sees it.
 * After the save the server says whether the address answered; the editor
 * passes that on as a word of warning, never as a reason not to save.
 */
const props = defineProps({
  link: { type: Object, default: null },
  // Hub members [{ id, label, email, img }] for the audience picker.
  members: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved', 'delete']);

const everyone = () => ({ all_participants: true, member_ids: [], segment_ids: [], broker_ids: [] });
const accessOf = (source) => (source
  ? {
    all_participants: !!source.all_participants,
    member_ids: [...(source.member_ids || [])],
    segment_ids: [...(source.segment_ids || [])],
    broker_ids: [...(source.broker_ids || [])],
  }
  : everyone());
const isEveryone = (a) => !a || a.all_participants;

const initialForm = () => ({
  title: props.link?.title ?? '',
  description: props.link?.description ?? '',
  link: props.link?.link ?? '',
  image_url: props.link?.image_url ?? '',
});

const form = reactive(initialForm());
const access = ref(accessOf(props.link?.access));
const initialSnapshot = JSON.stringify({ form: initialForm(), access: accessOf(props.link?.access) });
const linkTouched = ref(false);
const pickingAudience = ref(false);
const showDiscardConfirm = ref(false);
const isSaving = ref(false);
const serverErrors = ref({});

// JPG or PNG up to 10 MB, said here before the upload; the server says it again.
const IMAGE_TYPES = ['image/jpeg', 'image/png'];
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const savedImageId = props.link?.image_media_id ?? null;
const imageInput = ref(null);
const {
  id: imageId, uploading: imageUploading, error: imageError, upload, remove: removeImage, keep: keepImage, hold: holdImage, release: releaseImage,
} = useStagedImage('link', toRef(form, 'image_url'), { initialId: savedImageId });

const pickImage = (event) => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (!IMAGE_TYPES.includes(file.type)) imageError.value = 'The image must be a JPG or PNG.';
  else if (file.size > IMAGE_MAX_BYTES) imageError.value = 'The image may not be bigger than 10 MB.';
  else upload(file);
};

const { segments, loadHubSegments } = useHubSegments();
onMounted(loadHubSegments);
const { loadHubBrokers } = useHubBrokers();
watch(() => access.value.broker_ids.length, (count) => { if (count) loadHubBrokers(); }, { immediate: true });
const authStore = useAuthStore();
const book = computed(() => bookFor(props.link, authStore.userData));
const audienceSummary = computed(() => accessSummaryLine(access.value, props.members, segments.value, { book: book.value, me: ownBookOf(authStore.userData) }));

const heading = computed(() => (props.link ? 'Edit link' : 'New link'));

const people = (n) => `${new Intl.NumberFormat().format(n)} ${n === 1 ? 'person' : 'people'}`;
const audienceReach = ref(props.link?.reach ?? null);

// What the hub will show under this address, once it has one.
const previewHost = computed(() => {
  try {
    return new URL(form.link.trim()).host;
  } catch {
    return '';
  }
});

const isDirty = computed(() => JSON.stringify({ form: { ...form }, access: access.value }) !== initialSnapshot);
useUnsavedChanges({ key: 'link-editor', dirty: isDirty, onSectionHome: () => emit('close') });

// People paste "www.example.com"; the server assumes https the same way.
const onLinkBlur = () => {
  linkTouched.value = true;
  const url = (form.link || '').trim();
  if (url && !/^[a-z][a-z0-9+.-]*:/i.test(url) && isValidHttpUrl(`https://${url}`)) {
    form.link = `https://${url}`;
  }
};

const linkError = computed(() => {
  const url = (form.link || '').trim();
  if (!url) return 'Where should this link go?';
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !isValidHttpUrl(url)) return 'The link must start with http:// or https://.';
  return '';
});

const canSave = computed(() => form.title.trim() !== '' && form.title.length <= 60 && (form.description || '').length <= 200 && !linkError.value && !imageUploading.value);

const errorFor = (field) => {
  const list = serverErrors.value?.[field];
  return Array.isArray(list) ? list[0] : '';
};

// The upload's own refusal, or the save's ("Upload it again"), in place of the hint.
const IMAGE_HINT = 'JPG or PNG · max 10 MB';
const imageNote = computed(() => imageError.value || errorFor('image_media_id') || IMAGE_HINT);

const onAudience = (chosen) => {
  access.value = accessOf(chosen);
  if (chosen?.reach !== undefined) audienceReach.value = chosen.reach;
  pickingAudience.value = false;
};

const accessChanged = computed(() => JSON.stringify(access.value) !== JSON.stringify(accessOf(props.link?.access)));

// Every way out of a save: the image it sent is no longer the save's to hold.
const stopSaving = () => {
  isSaving.value = false;
  releaseImage();
};

const sendLink = async (api) => {
  const payload = { title: form.title.trim(), description: (form.description || '').trim() || null, link: form.link.trim() };
  // Sent only when it changed: absent leaves the saved image alone.
  if (imageId.value !== savedImageId) payload.image_media_id = imageId.value;

  const res = props.link?.id
    ? await axios.patch(`${api}/event-links/${props.link.id}`, payload)
    : await axios.post(`${api}/event-links`, payload);
  return res.data?.data ?? null;
};

const reportSaveFailure = (error) => {
  const data = error.response?.data;
  serverErrors.value = data?.errors ?? (typeof data?.message === 'object' ? data.message : {});
  window.loadNotification?.({ message: 'An error occurred while saving the link.', error: true });
  stopSaving();
};

// A link meant for some people must not go out to everyone, so a failed audience write undoes it.
const undoNewLink = async (api, id, saved) => {
  try {
    await axios.delete(`${api}/event-links/${id}`);
    // The server deleted the link's image with it, so it has to be uploaded again.
    const hadImage = imageId.value !== null;
    keepImage();
    removeImage();
    window.loadNotification?.({
      message: hadImage
        ? "Couldn't set who sees it, so the link and its image were not saved. Add the image again and try once more."
        : "Couldn't set who sees it, so the link was not saved. Try again.",
      error: true,
    });
  } catch (undoError) {
    console.error(undoError);
    window.loadNotification?.({ message: 'The link was saved, but everyone sees it. Set who sees it from the list.', error: true });
    keepImage();
    emit('saved', saved);
    emit('close');
  }
};

const needsAccessWrite = (id) => id && (props.link?.id ? accessChanged.value : !isEveryone(access.value));

const writeAccess = async (api, saved) => {
  const editing = !!props.link?.id;
  const id = saved?.id ?? props.link?.id;
  if (!needsAccessWrite(id)) return false;
  try {
    await axios.put(`${api}/content-access/link/${id}`, access.value);
    return false;
  } catch (error) {
    console.error(error);
    if (editing) {
      window.loadNotification?.({ message: 'The link was saved, but who sees it did not change. Try again from the list.', error: true });
      return false;
    }
    await undoNewLink(api, id, saved);
    return true;
  }
};

const save = async () => {
  if (!canSave.value || isSaving.value) return;

  const api = window.store.event_backend_api;
  isSaving.value = true;
  holdImage();
  serverErrors.value = {};

  let saved = null;
  try {
    saved = await sendLink(api);
  } catch (error) {
    reportSaveFailure(error);
    return;
  }

  if (await writeAccess(api, saved)) {
    stopSaving();
    return;
  }

  window.loadNotification?.(saved?.reachable === false
    ? { message: "Saved. We couldn't reach that address just now — worth checking.", error: true }
    : { message: 'Link was saved successfully!' });
  keepImage();
  emit('saved', saved);
  emit('close');
  stopSaving();
};

const cancel = () => {
  // The save is still deciding what is kept; leaving now could lose its image.
  if (isSaving.value) return;
  if (isDirty.value) {
    showDiscardConfirm.value = true;
    return;
  }
  emit('close');
};

const onKeydown = (e) => {
  if (e.key === 'Escape' && !pickingAudience.value) cancel();
};

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

defineExpose({ form, access, canSave, save, cancel, onLinkBlur, showDiscardConfirm });
</script>

<template>
  <ContentPageShell :title="heading" back-label="Back to links" @back="cancel">
    <div class="link-form link-form--page">
      <div class="link-form__fields">
        <div class="link-form__image-field">
          <span class="link-form__label">Image <span class="link-form__optional">— optional</span></span>
          <div class="link-form__image">
            <span class="link-form__image-tile" aria-hidden="true">
              <img v-if="form.image_url" :src="form.image_url" alt="">
              <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
            </span>
            <div>
              <div class="link-form__image-actions">
                <XlButton variant="ghost" size="sm" :loading="imageUploading" :disabled="isSaving" data-testid="link-image-upload" @click="imageInput?.click()">
                  {{ form.image_url ? 'Replace image' : 'Upload an image' }}
                </XlButton>
                <XlButton v-if="form.image_url" variant="ghost-danger" size="sm" :disabled="isSaving" data-testid="link-image-remove" @click="removeImage">Remove</XlButton>
              </div>
              <p class="link-form__note" :class="{ 'link-form__note--error': imageNote !== IMAGE_HINT }" :role="imageNote !== IMAGE_HINT ? 'alert' : null">
                {{ imageNote }}
              </p>
            </div>
            <input ref="imageInput" type="file" accept="image/png,image/jpeg" class="link-form__file" aria-label="Image" data-testid="link-image-input" @change="pickImage">
          </div>
        </div>
        <XlField v-model="form.title" label="Title" name="title" required maxlength="60" :error="errorFor('title')" :autofocus="!props.link" />
        <XlField v-model="form.description" type="textarea" label="Description" name="description" maxlength="200" hint="What they will find there" :error="errorFor('description')" />
        <XlField
          v-model="form.link"
          type="url"
          label="Link"
          name="link"
          required
          placeholder="example.com/page"
          hint="https:// is added for you · every link opens in a new tab, so nobody loses their place in the portal"
          :error="(linkTouched && linkError) || errorFor('link')"
          @blur="onLinkBlur"
        />
      </div>

      <aside class="link-form__rail">
        <div class="link-form__preview">
            <h3 class="link-form__heading">What policyholders will see</h3>
            <div class="link-form__preview-row">
              <span class="link-form__preview-icon" aria-hidden="true">
                <img v-if="form.image_url" :src="form.image_url" alt="">
                <svg v-else width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></svg>
              </span>
              <span class="link-form__preview-text">
                <b>{{ form.title || 'Your title' }}</b>
                <small v-if="form.description">{{ form.description }}</small>
                <small>{{ previewHost || 'example.com' }}</small>
              </span>
              <span class="link-form__preview-out" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></svg>
              </span>
            </div>
          </div>

          <div class="link-form__who">
            <h3 class="link-form__heading">Who sees it</h3>
            <div class="link-form__who-row">
              <span class="link-form__who-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 19v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 17.4V19" /><circle cx="9.5" cy="7.5" r="3.1" /><path d="M21 19v-1.6a3.4 3.4 0 0 0-2.6-3.3M15.4 4.6a3.4 3.4 0 0 1 0 6.1" /></svg>
              </span>
              <span class="link-form__who-text">
                <b>{{ audienceSummary }}</b>
                <small v-if="audienceReach !== null">{{ people(audienceReach) }}</small>
              </span>
              <XlButton variant="ghost" size="sm" @click="pickingAudience = true">Change</XlButton>
            </div>
          </div>

          <div class="link-form__page-actions">
            <XlButton :disabled="!canSave || isSaving" @click="save">Save link</XlButton>
            <XlButton variant="ghost" @click="cancel">Cancel</XlButton>
            <XlButton v-if="props.link" variant="ghost-danger" @click="emit('delete')">Delete this link</XlButton>
          </div>
      </aside>

      <div v-if="showDiscardConfirm" class="link-form__discard">
        <p>Discard unsaved changes?</p>
        <div>
          <XlButton variant="ghost" size="sm" @click="showDiscardConfirm = false">Keep editing</XlButton>
          <XlButton variant="danger" size="sm" @click="emit('close')">Discard</XlButton>
        </div>
      </div>
    </div>

    <ManageAccessModal
      v-if="pickingAudience"
      :resource="{ name: form.title, access }"
      :members="members"
      :book="book"
      subject-type="link"
      @close="pickingAudience = false"
      @saved="onAudience"
    />
  </ContentPageShell>
</template>

<style scoped>
.link-form { display: flex; flex-direction: column; row-gap: 16px; }

.link-form__rail,
.link-form__fields { display: contents; }

/* Same fields in the same order; CSS puts the rail beside them. */
.link-form--page {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 22px;
  align-items: start;
}
.link-form--page > * { grid-column: 1; }

.link-form--page > .link-form__fields {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 26px 28px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-lg, 16px);
  background: #fff;
}

.link-form--page > .link-form__rail {
  grid-column: 2;
  grid-row: 1 / span 99;
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-lg, 16px);
  background: #fff;
}

.link-form__heading {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: var(--xl-color-primary-text, #1d1b20);
}

.link-form__preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-md, 12px);
  background: var(--xl-color-background, #faf8fc);
}

.link-form__preview-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--xl-radius-md, 12px);
  background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 8%, white);
  color: var(--xl-color-primary, #5e2f78);
}

.link-form__preview-icon img { width: 100%; height: 100%; object-fit: cover; border-radius: inherit; }

.link-form__preview-text { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.link-form__preview-out { flex: none; color: var(--xl-color-secondary-text, #49454f); display: inline-flex; }

/* Prototype linkForm (build 16 Oct): a 78px tile, the buttons, and the note under them. */
.link-form__image-field { display: flex; flex-direction: column; gap: 6px; }
.link-form__label {
  font-size: var(--xl-type-label-large-size);
  line-height: var(--xl-type-label-large-line);
  font-weight: 500;
  color: var(--xl-color-secondary-text);
}
.link-form__optional { font-weight: 400; color: var(--xl-color-outline); }
.link-form__image { display: flex; align-items: center; gap: 14px; }
.link-form__image-tile {
  width: 78px;
  height: 78px;
  flex: 0 0 78px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 14px;
  background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 8%, white);
  color: var(--xl-color-primary, #5e2f78);
}
.link-form__image-tile img { width: 100%; height: 100%; object-fit: cover; }
.link-form__image-actions { display: flex; flex-wrap: wrap; gap: 7px; }
.link-form__note { margin: 7px 0 0; font-size: var(--xl-type-body-small-size); color: var(--xl-color-outline); }
.link-form__note--error { color: var(--xl-color-error); }
.link-form__file { display: none; }

.link-form__who-row { display: flex; align-items: center; gap: 12px; }

.link-form__who-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--xl-radius-md, 12px);
  background: var(--xl-color-success-container, #e4f3eb);
  color: var(--xl-color-success, #166b48);
}

.link-form__who-text { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.link-form__who-text small { color: var(--xl-color-secondary-text, #49454f); }
.link-form__preview-text small { color: var(--xl-color-secondary-text, #49454f); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.link-form__page-actions { display: flex; flex-direction: column; gap: 9px; }
.link-form__page-actions :deep(.xl-btn) { width: 100%; }

@media (max-width: 900px) {
  .link-form--page { display: flex; flex-direction: column; }
  .link-form--page > .link-form__rail { position: static; }
}

.link-form__audience {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-md, 12px);
  font-size: var(--xl-type-body-medium-size, 14px);
}

.link-form__discard {
  padding: 14px 16px;
  border-radius: var(--xl-radius-md, 12px);
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.link-form__discard p { margin: 0; font-weight: 600; }
.link-form__discard div { display: flex; gap: 8px; }
</style>
